import type { ProductOverview } from "@nova-ai/product-detail-contract"
import { ImageOff } from "lucide-react"
import { useState } from "react"

import { cn } from "../../lib/utils"
import { Callout } from "../ui/callout"

type ProductOverviewPanelProps = {
  overview: ProductOverview | undefined
  isLoading: boolean
  error: unknown
}

export function ProductOverviewPanel({ overview, isLoading, error }: ProductOverviewPanelProps) {
  if (isLoading) {
    return (
      <section
        className="flex gap-3 rounded-xl bg-card p-4 shadow-card"
        aria-label="Loading product overview"
      >
        <div className="h-40 w-full animate-pulse rounded-lg bg-muted/60 motion-reduce:animate-none sm:size-48" />
      </section>
    )
  }
  if (error || !overview) {
    return (
      <Callout tone="destructive" title="Product overview could not be loaded">
        Values, Visuals, and Keywords remain available below.
      </Callout>
    )
  }

  return (
    <section aria-label="Catalog photos" className="rounded-xl bg-card p-4 shadow-card">
      <ProductCatalogGallery images={overview.images} />
    </section>
  )
}

function ProductCatalogGallery({ images }: { images: ProductOverview["images"] }) {
  const [selectedUrl, setSelectedUrl] = useState(images[0]?.url ?? null)
  const selected = images.find((image) => image.url === selectedUrl) ?? images[0] ?? null
  if (!selected) {
    return (
      <div className="flex min-h-32 items-center justify-center rounded-lg bg-muted/35 text-muted-foreground">
        <span className="flex flex-col items-center gap-2 text-xs">
          <ImageOff className="size-6" />
          No catalog photos
        </span>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
      <div className="flex h-40 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted/35 p-3 sm:size-48">
        <img
          src={selected.url}
          alt="Selected catalog product"
          className="size-full object-contain"
        />
      </div>

      {images.length > 1 ? (
        <div
          className="flex min-w-0 gap-2 overflow-x-auto p-1 sm:max-h-48 sm:flex-wrap sm:overflow-y-auto"
          aria-label="Catalog photos"
        >
          {images.map((image, index) => (
            <button
              key={image.url}
              type="button"
              aria-label={`Show catalog photo ${index + 1}`}
              aria-pressed={image.url === selected.url}
              onClick={() => setSelectedUrl(image.url)}
              className={cn(
                "flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted/35 p-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                image.url === selected.url ? "ring-1 ring-foreground/30" : "hover:bg-muted/60"
              )}
            >
              <img src={image.url} alt="" className="size-full object-contain" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
